import downloadRecoveryCodes from '@Application/Security/Authentications/downloadRecoveryCodes';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { Download } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, Checkbox, Message } from '@verclam/ui-kit';

const listStyle = css`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 0.5rem;
	list-style: none;
	margin: 1rem 0;
	padding: 0;
`;

const codeItemStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.medium};
	background-color: ${theme.colors.surfaceMuted};
	border-radius: ${theme.radii.small};
	padding: 0.5rem 0.75rem;
	text-align: center;
`;

const actionsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-top: 1rem;
`;

type Props = {
	readonly recoveryCodes: readonly string[];
	readonly onFinish: () => void;
};

/**
 * Last step of the enrollment wizard: the 10 codes are shown once, from the local state the caller
 * received straight off the confirmation response — they never touch a store. `Continuer` (the
 * screen's one and only exit, gated on the confirmation checkbox) is disabled until the visitor
 * acknowledges they kept them.
 */
const RecoveryCodesStep: React.FC<Props> = ({ recoveryCodes, onFinish }) => {
	const checkboxId = React.useId();
	const dispatch = useDispatch();
	const [isConfirmed, setIsConfirmed] = React.useState(false);

	const handleConfirmedChange = React.useCallback((checked: boolean) => {
		setIsConfirmed(checked);
	}, []);

	const handleDownload = React.useCallback(() => {
		dispatch(downloadRecoveryCodes(recoveryCodes));
	}, [dispatch, recoveryCodes]);

	return (
		<React.Fragment>
			<Message
				severity="warn"
				text="Conservez ces codes en lieu sûr : ils ne seront plus jamais affichés."
			/>
			<ul css={listStyle}>
				{recoveryCodes.map((code) => (
					<li key={code} css={codeItemStyle}>
						{code}
					</li>
				))}
			</ul>
			<div css={actionsStyle}>
				<Button
					type="button"
					label="Télécharger les codes"
					icon={<Download size={16} />}
					outlined
					onClick={handleDownload}
				/>
				<Checkbox
					name="confirmSaved"
					id={checkboxId}
					label="J’ai conservé mes codes de récupération"
					checked={isConfirmed}
					onChange={handleConfirmedChange}
				/>
				<Button type="button" label="Continuer" disabled={!isConfirmed} onClick={onFinish} />
			</div>
		</React.Fragment>
	);
};

export default RecoveryCodesStep;
